import type { AudioFormat, SampleRate } from '@namu/protocol'
import workletUrl from './captureWorklet.js?url'
import { createOpusEncoder } from './codec'
import type { OpusEncoder } from './codec'

/**
 * Turning the microphone into frames on the wire.
 *
 * The chain is: the existing MediaStream, into an AudioContext running at the
 * rate the server wants, into a worklet that buffers twenty milliseconds at a
 * time, into an encoder, out as frames.
 *
 * Two things here are worth reading twice.
 *
 * The stream is borrowed, never opened. The session already holds one for the
 * level meter, and a second `getUserMedia` on a phone can steal the first one's
 * audio route, leaving the orb reading silence while capture hears fine.
 *
 * The context is created at 16 kHz rather than resampling afterwards. The
 * browser then does the conversion in native code on the audio thread, which is
 * both faster and better than anything achievable in JavaScript, and it means
 * the worklet already emits exactly what the protocol asked for.
 */

/** The rate speech recognisers want, and half the bytes of 24 kHz. */
export const CAPTURE_SAMPLE_RATE: SampleRate = 16000

/** Matches the server's Opus setting, so neither end has to re-chunk. */
const BITRATE = 24_000

export interface Capture {
  /** True while frames are being produced. */
  readonly active: boolean
  /** The format actually being produced, after negotiation with the browser. */
  readonly format: AudioFormat
  /** Stops producing frames without tearing anything down. */
  pause(): void
  resume(): void
  close(): Promise<void>
}

export interface CaptureOptions {
  stream: MediaStream
  /** `opus` when the browser can encode it. Falls back on its own if not. */
  codec: AudioFormat['codec']
  /** Called with each frame, ready to go on the wire. */
  onFrame(payload: Uint8Array): void
}

export async function createCapture(options: CaptureOptions): Promise<Capture> {
  const { stream, onFrame } = options

  const context = new AudioContext({ sampleRate: CAPTURE_SAMPLE_RATE })
  // Safari starts a context suspended when it was not created directly inside a
  // gesture handler. Resuming is harmless when it is already running.
  if (context.state === 'suspended') await context.resume()

  await context.audioWorklet.addModule(workletUrl)

  const source = context.createMediaStreamSource(stream)
  const node = new AudioWorkletNode(context, 'namu-capture', {
    numberOfInputs: 1,
    numberOfOutputs: 0,
    channelCount: 1,
  })
  source.connect(node)

  let encoder: OpusEncoder | null = null
  let codec: AudioFormat['codec'] = 'pcm16'

  if (options.codec === 'opus') {
    encoder = await createOpusEncoder(CAPTURE_SAMPLE_RATE, BITRATE, onFrame)
    // Falling back rather than failing. A browser that cannot encode Opus can
    // still hold a conversation; it just costs more data.
    if (encoder) codec = 'opus'
  }

  let paused = false

  node.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
    if (paused) return
    const pcm = new Int16Array(event.data)
    if (encoder) encoder.encode(pcm)
    else onFrame(new Uint8Array(event.data))
  }

  return {
    get active() {
      return !paused && context.state === 'running'
    },

    get format() {
      // Reports what is actually being produced, which is not always what was
      // asked for. The caller has to tell the server the truth.
      return { codec, sampleRate: CAPTURE_SAMPLE_RATE }
    },

    pause() {
      paused = true
    },

    resume() {
      paused = false
      if (context.state === 'suspended') void context.resume()
    },

    async close() {
      paused = true
      node.port.onmessage = null
      try {
        source.disconnect()
        node.disconnect()
      } catch {
        // Already disconnected.
      }
      encoder?.close()
      try {
        await context.close()
      } catch {
        // Already closed.
      }
    },
  }
}
